'use client';

import { type DocumentSuggestionPayload } from '@/helpers/clinical-tasks/documentCreationTask';
import { useDocumentCreationSuggestionBanner } from '@/hooks/useDocumentCreationSuggestionBanner';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import {
  ChevronLeft,
  ChevronRight,
  EyeOff,
  FileText,
  MoreHorizontal,
  X,
} from 'lucide-react';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

export interface DocumentSuggestionBannerProps {
  sessionId: string | undefined;
  /** Runs after analytics + per-task dismissal (e.g. send message, open Evidence panel). */
  onCtaClick: (item: DocumentSuggestionPayload) => void;
  className?: string;
}

export function DocumentSuggestionBanner({
  sessionId,
  onCtaClick,
  className,
}: DocumentSuggestionBannerProps) {
  const intl = useIntl();

  const {
    shouldShowBanner,
    suggestions,
    dismissSuggestionByTaskId,
    trackCarouselNav,
    trackCta,
    hideAllSuggestions,
  } = useDocumentCreationSuggestionBanner(sessionId);

  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const count = suggestions.length;

  useEffect(() => {
    setActiveIndex((i) => (count > 0 ? Math.min(i, count - 1) : 0));
  }, [count]);

  const current = suggestions[Math.min(activeIndex, Math.max(0, count - 1))];

  const goNext = useCallback(() => {
    if (count < 2) return;
    const next = (activeIndex + 1) % count;
    setActiveIndex(next);
    trackCarouselNav('next', next);
  }, [activeIndex, count, trackCarouselNav]);

  const goPrev = useCallback(() => {
    if (count < 2) return;
    const prev = (activeIndex - 1 + count) % count;
    setActiveIndex(prev);
    trackCarouselNav('prev', prev);
  }, [activeIndex, count, trackCarouselNav]);

  const handleCtaClick = useCallback(
    (item: DocumentSuggestionPayload) => {
      trackCta(item);
      dismissSuggestionByTaskId(item.taskId);
      onCtaClick(item);
    },
    [dismissSuggestionByTaskId, onCtaClick, trackCta]
  );

  const handleDismissCurrent = useCallback(() => {
    if (current) {
      dismissSuggestionByTaskId(current.taskId);
    }
  }, [current, dismissSuggestionByTaskId]);

  if (!shouldShowBanner || !current) {
    return null;
  }

  const headlineFull = current.bannerTitle.trim();
  const subtitleFull = current.bannerSubtitle.trim();
  const hideSuggestionsLabel = intl.formatMessage({
    defaultMessage: 'Hide these suggestions',
    id: '72r1cOOSWR',
  });

  return (
    <div
      className={cn('flex items-center gap-2', className)}
      data-testid="document-suggestion-banner"
    >
      {count > 1 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconOnly
          className="shrink-0 rounded-full"
          onClick={goPrev}
          aria-label={intl.formatMessage({
            defaultMessage: 'Previous suggestion',
            id: '6Z9QsHDsqD',
          })}
        >
          <ChevronLeft className="size-4 rtl:-scale-x-100" />
        </Button>
      ) : null}

      <div
        className={cn(
          'flex min-w-0 flex-1 items-center gap-4 rounded-xl border border-border',
          'bg-surface-accent px-4 py-3 shadow-[0px_2px_2px_0px_rgb(var(--shadow-color)/0.04),0px_3px_8px_2px_rgb(var(--shadow-color)/0.08)]'
        )}
      >
        <FileText className="size-5 shrink-0 text-text-tertiary" aria-hidden />

        <div className="min-w-0 flex-1 overflow-hidden">
          <AnimatePresence initial={false} mode="wait">
            <m.div
              key={current.taskId}
              initial={reduceMotion ? false : { opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, x: -8 }}
              transition={{ duration: reduceMotion ? 0 : 0.2, ease: 'easeOut' }}
              className="min-w-0"
            >
              {headlineFull ? (
                <TypographyP2SemiBold className="truncate text-text-primary">
                  {headlineFull}
                </TypographyP2SemiBold>
              ) : null}
              {subtitleFull ? (
                <TypographyCaption className="mt-0.5 text-wrap text-text-secondary">
                  {subtitleFull}
                </TypographyCaption>
              ) : null}
            </m.div>
          </AnimatePresence>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => handleCtaClick(current)}
          >
            {current.cta?.trim()}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xxs"
            iconOnly
            onClick={handleDismissCurrent}
            aria-label={intl.formatMessage({
              defaultMessage: 'Dismiss document suggestion',
              id: '5PckQWBOWp',
            })}
          >
            <X size={14} />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="xxs"
                iconOnly
                aria-label={intl.formatMessage({
                  defaultMessage: 'More document suggestion options',
                  id: 'TH9yxNqevg',
                })}
              >
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onSelect={() => hideAllSuggestions(hideSuggestionsLabel)}>
                <EyeOff size={14} />
                <span>{hideSuggestionsLabel}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {count > 1 ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          iconOnly
          className="shrink-0 rounded-full"
          onClick={goNext}
          aria-label={intl.formatMessage({
            defaultMessage: 'Next suggestion',
            id: 'Rvj+oyMGA9',
          })}
        >
          <ChevronRight className="size-4 rtl:-scale-x-100" />
        </Button>
      ) : null}
    </div>
  );
}
